import React from 'react';
import { useTheme } from 'styled-components';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { getOpacity } from '../../../theme/utils/get-rgb';
import { cn } from '../../../utils/tailwind';
import { createStableClassToken } from '../../../utils/stable-class-token';

interface SkeletonProps {
    width?: number | string | object;
    height?: number | string | object;
    radius?: number;
    variant?: 'default' | 'negative' | 'positive';
}

const getSizeValue = (value: string | number) => (typeof value === 'number' ? `${value}rem` : value);

const getResponsiveCss = (
    className: string,
    property: 'width' | 'height',
    value: object,
    theme: ReturnType<typeof useTheme>,
) => {
    const entries = Object.entries(value) as [BreakpointSizeType, string | number][];

    return entries
        .map(([breakpoint, responsiveValue], index) => {
            const size = getSizeValue(responsiveValue);
            const mediaQuery = index === 0 ? 'screen and (min-width: 0px)' : theme.breakpoints[breakpoint].min;
            const minProperty = property === 'width' ? 'min-width' : 'min-height';

            return `
                @media ${mediaQuery} {
                    .${className} {
                        ${property}: ${size};
                        ${minProperty}: ${size};
                    }
                }
            `;
        })
        .join('\n');
};

const Skeleton: React.FC<SkeletonProps> = ({ width = 10, height = 1.6, radius = 0.5, variant = 'default' }) => {
    const theme = useTheme();
    const skeletonClassName = createStableClassToken('skeleton', [width, height, radius, variant]);

    const baseStyles: React.CSSProperties = {
        borderRadius: `${radius}rem`,
        backgroundColor: getOpacity(theme.palette.color[variant].dark, 0.3),
    };

    if (typeof width === 'number' || typeof width === 'string') {
        const parsedWidth = getSizeValue(width);

        baseStyles.width = parsedWidth;
        baseStyles.minWidth = parsedWidth;
    }

    if (typeof height === 'number' || typeof height === 'string') {
        const parsedHeight = getSizeValue(height);

        baseStyles.height = parsedHeight;
        baseStyles.minHeight = parsedHeight;
    }

    const responsiveCss = [
        typeof width === 'object' ? getResponsiveCss(skeletonClassName, 'width', width, theme) : '',
        typeof height === 'object' ? getResponsiveCss(skeletonClassName, 'height', height, theme) : '',
    ]
        .filter(Boolean)
        .join('\n');

    return (
        <>
            <span
                className={cn(
                    skeletonClassName,
                    'relative float-left inline-flex overflow-hidden after:absolute after:inset-0 after:animate-[app-skeleton-wave_1.5s_linear_0.5s_infinite] after:bg-[linear-gradient(90deg,transparent,var(--skeleton-wave-color),transparent)] after:content-[""]',
                )}
                style={
                    {
                        ...baseStyles,
                        '--skeleton-wave-color': theme.palette.color.default.opacity.high,
                    } as React.CSSProperties
                }
            />
            {responsiveCss && <style>{responsiveCss}</style>}
        </>
    );
};

export default Skeleton;
